import { useSearchParams } from 'next/navigation';
import {
	FiltersStripe,
	InboxHeadFilters,
	MobileInboxList,
} from '@/components/dashboard';
import SimpleTable from '@/components/common/tables/simpleTable';
import {
	Button,
	EmptyBox,
	FunnelEmptyScreen,
	Icon,
	LoaderScreen,
	Typography,
} from '@/components/common';
import { TableData } from '@/interfaces/inbox';
import Icons from '@/const/icons';
import { useRouter } from 'next/router';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
	GET_LAWYER_INBOX_LIST,
	GET_LAWYER_CASES,
	GET_LAWYER_CLIENTS,
	GET_QUESTIONS_SUBJECTS,
} from '@/api';
import { inboxService } from '@/api/inbox';
import { InboxAdapter } from '@/adapters';
import { useFilters, useNotification } from '@/hooks';
import { useEventSource } from '@/hooks/useEventSource';
import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import { ModalResolved } from '../../dashboard/inbox/ModalResolved';
import { CheckCircle } from '@/public/icons/tsx';
import clsx from 'clsx';
import AppRoutes from '@/const/routes';
import { formatDateWithin24h } from '@/lib';

interface Props {
	setOpenNewMessage: (value: boolean) => void;
}

export interface DateI {
	from: string;
	to: string;
}

export const InboxList: React.FC<Props> = ({ setOpenNewMessage }) => {
	const LIMIT_PER_PAGE = 9;
	const router = useRouter();
	const session = useSession();
	const token = session.data?.accessToken;
	const searchParams = useSearchParams();
	const { toastSuccess, toastError } = useNotification();
	const params = new URLSearchParams(searchParams);
	const { setParamSeveralFilters, setParamFilter, removeParaFilter } =
		useFilters();
	const pathname = router.pathname;
	const [resConversationId, setResConversationId] = useState<
		string | undefined
	>();

	const {
		tableDataAdapter,
		subjectFilterAdapter,
		clientFilterAdapter,
		caseFilterAdapter,
		dateFilterAdapter,
	} = InboxAdapter;

	const search = params.get('search') ?? '';
	const status = params.get('view') ?? '';
	const client = params.get('clients') ?? '';
	const subject = params.get('subject') ?? '';
	const startDate = params.get('startDate') ?? '';
	const endDate = params.get('endDate') ?? '';
	const queryCases = params.get('cases') ?? '';
	const currentPage = params.get('page') ?? 1;

	const { data } = useEventSource({
		url: `${process.env.NEXT_PUBLIC_API}/sse/inbox/subscribe?token=${token}`,
	});

	const {
		data: inbox,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [
			GET_LAWYER_INBOX_LIST,
			currentPage,
			search,
			client,
			subject,
			queryCases,
			status,
			startDate,
			endDate,
		],
		queryFn: () =>
			inboxService.getLawyerInboxList({
				page: Number(currentPage),
				limit: LIMIT_PER_PAGE,
				search: search,
				status: status,
				clientId: client,
				subjectId: subject,
				cases: queryCases,
				startDate: startDate,
				endDate: endDate,
				// aIEnabled: true,
			}),
		enabled: !!currentPage,
		refetchOnWindowFocus: false,
	});

	const [subjects, cases, clients] = useQueries({
		queries: [
			{
				queryKey: [GET_QUESTIONS_SUBJECTS],
				queryFn: () => inboxService.getInboxSubjects(),
			},
			{
				queryKey: [GET_LAWYER_CASES],
				queryFn: () => inboxService.getLawyerCases(),
			},
			{
				queryKey: [GET_LAWYER_CLIENTS],
				queryFn: () => inboxService.getLawyerClients(),
			},
		],
	});

	useEffect(() => {
		if (data && currentPage === '1') {
			refetch();
		}
	}, [data]);

	const setDate = (value: DateI | undefined): void => {
		if (value === undefined) {
			removeParaFilter('startDate');
			removeParaFilter('endDate');
		}

		if (value) {
			setParamSeveralFilters([
				{
					label: 'startDate',
					value: value.from,
				},
				{
					label: 'endDate',
					value: value.to,
				},
			]);
		}
	};

	const total = inbox?.data?.total ?? 0;
	const totalItems = inbox?.data?.totalItems ?? 0;
	const totalPages = inbox?.data?.totalPages ?? 1;

	const listSubjects = subjects.data?.data?.items ?? [];
	const listCases = cases.data?.data?.items ?? [];
	const listClients = clients.data?.data?.items ?? [];
	const listConversations = inbox?.data?.items ?? [];

	const tableData = tableDataAdapter(listConversations);
	const subjectFilters = subjectFilterAdapter(listSubjects);
	const clientFilters = clientFilterAdapter(listClients);
	const casesFilters = caseFilterAdapter(listCases);
	const dateFilters = dateFilterAdapter(startDate, endDate, setDate);

	const objectFilters = {
		Clients: clientFilters,
		Subject: subjectFilters,
		Cases: casesFilters,
		Date: dateFilters,
	};

	const onClickResolved = async (callback: any): Promise<void> => {
		if (!resConversationId) return;
		try {
			await inboxService.resolvedConversation(resConversationId);
			callback();
			// await queryClient.refetchQueries({
			// 	queryKey: [GET_INBOX_LIST],
			// });
			toastSuccess('This message has been successfully marked as Resolved.');
			setResConversationId(undefined);
			refetch();
		} catch (error: any) {
			toastError('An error has occurred. Please try again.');
		}
	};

	const onClickUnread = async (id: string): Promise<void> => {
		try {
			await inboxService.markConversationUnread(id);
			refetch();
		} catch (error) {
			console.log('Error', error);
		}
	};

	const onClickRead = async (id: string): Promise<void> => {
		try {
			await inboxService.markConversationRead(id);
			refetch();
		} catch (error) {
			console.log('Error', error);
		}
	};

	const onClickRow = (rowValue: TableData): void => {
		const allFilteredIds = tableData.map((item) => item.id);
		const currentFilteredIndex = allFilteredIds.findIndex(
			(id) => id === rowValue.id
		);

		router.push({
			pathname: `/lawyerapp/inbox/${rowValue.id}`,
			query: {
				page: currentPage,
				limit: LIMIT_PER_PAGE,
				conversationId: rowValue.id,
				fids: JSON.stringify(allFilteredIds),
				cidx: currentFilteredIndex.toString(),
			},
		});
	};

	const parsedCases = [
		{
			icon: <Icon src={Icons.smsLinear} className="!h-5 !w-5" />,
			value: '',
			label: 'View All',
		},
		{
			icon: <Icon src={Icons.smsNotification} className="!h-5 !w-5" />,
			value: 'unread',
			label: 'Unread Only',
		},
	];

	const loading =
		isLoading || subjects.isLoading || cases.isLoading || clients.isLoading;

	const hasFilter = status || client || subject || queryCases;

	return (
		<>
			<InboxHeadFilters
				total={total}
				totalItems={totalItems}
				cases={parsedCases}
				filters={objectFilters}
				setOpenNewMessage={setOpenNewMessage}
			/>
			<FiltersStripe
				filters={objectFilters}
				handleSelectFilters={(key, value) =>
					setParamFilter(key.toLowerCase(), value)
				}
				headersAlignEnd={[3]}
			/>
			{loading && <LoaderScreen className="mt-16 !justify-start" />}
			{tableData.length === 0 && !loading && !search && !hasFilter && (
				<>
					<EmptyBox
						message="You don't have any messages in your inbox right now."
						width={175}
						height={140}
						className={clsx({
							'!justify-start': pathname.includes(AppRoutes.LAWYERAPP),
						})}
					/>
					<div className="fixed bottom-6 right-4 flex justify-end md:hidden w-full">
						<Button
							size="medium"
							decoration="secondary"
							icon={Icons.arrowLeft}
							className="!py-4 !px-4 !w-44 bg-white shadow-[0px_4px_8px_0px_rgba(0,0,0,0.32)] md:shadow-none"
							onClick={() => setOpenNewMessage(true)}
						>
							<Typography
								type="semibold-16"
								className="flex items-center justify-center gap-3 w-full"
							>
								<Icon src={Icons.edit} className="!w-4 !h-4" />
								New Message
							</Typography>
						</Button>
					</div>
				</>
			)}
			{tableData.length === 0 && !loading && !search && hasFilter && (
				<>
					<FunnelEmptyScreen
						message="There are currently no emails matching this filter."
						className="max-sm:max-w-[315px] m-auto mt-4"
						classNameIcon="!w-[175px] !h-[140px] 3xl:!w-[200px] 3xl:!h-[160px]"
					/>
					<div className="fixed bottom-6 right-4 flex justify-end md:hidden w-full">
						<Button
							size="medium"
							decoration="secondary"
							icon={Icons.arrowLeft}
							className="!py-4 !px-4 !w-44 bg-white shadow-[0px_4px_8px_0px_rgba(0,0,0,0.32)] md:shadow-none"
							onClick={() => setOpenNewMessage(true)}
						>
							<Typography
								type="semibold-16"
								className="flex items-center justify-center gap-3 w-full"
							>
								<Icon src={Icons.edit} className="!w-4 !h-4" />
								New Message
							</Typography>
						</Button>
					</div>
				</>
			)}
			{tableData.length === 0 && !loading && search && (
				<>
					<div className={`text-center mt-4`}>
						<Icon
							src={Icons.searchCaseEmptyState}
							className="!w-[175px] !h-[140px] m-auto 3xl:!w-[200px] 3xl:!h-[160px]"
						/>
						<Typography type="regular-18" className="text-center">
							No results were found for your search.
						</Typography>
						<br />
						<Typography type="regular-18">
							Please try using different keywords.
						</Typography>
					</div>
					<div className="fixed bottom-6 right-4 flex justify-end md:hidden w-full">
						<Button
							size="medium"
							decoration="secondary"
							icon={Icons.arrowLeft}
							className="!py-4 !px-4 !w-44 bg-white shadow-[0px_4px_8px_0px_rgba(0,0,0,0.32)] md:shadow-none"
							onClick={() => setOpenNewMessage(true)}
						>
							<Typography
								type="semibold-16"
								className="flex items-center justify-center gap-3 w-full"
							>
								<Icon src={Icons.edit} className="!w-4 !h-4" />
								New Message
							</Typography>
						</Button>
					</div>
				</>
			)}
			{tableData.length > 0 && !loading && (
				<>
					<SimpleTable<TableData>
						hideRowActionHeader
						classNameHeader="hidden"
						classNameContainer="!rounded-t-2xl overflow-y-scroll border-none shadow-none
							hidden md:block !overflow-x-clip h-[53vh] 2xl:h-[55vh] md:max-h-[70vh]"
						classNameRows="grid grid-rows-4 sm:grid-rows-[auto]
							sm:grid-cols-[370px_1fr_1fr_100px_60px] 3xl:grid-cols-[470px_1fr_1fr_100px_60px]
							items-center cursor-pointer"
						columnsAlignEnd={[3]}
						classNameColumn="pr-0"
						disableHoverIndexesRowActionItems={(instance) => {
							if (instance.status !== 'OPEN') {
								return [1]; // this to disabled the "This conversation has been resolved and closed." option when the conversation is close and only work if there is 2 options
							} else return [];
						}}
						onClickRow={onClickRow}
						classNameRowActionItems={(instance) => {
							if (instance.status !== 'OPEN') {
								return 'w-[404px] bg-white rounded-lg';
							} else {
								return 'w-[210px] bg-white rounded-lg';
							}
						}}
						columns={[
							{
								header: '',
								content: (instance) => (
									<span className="capitalize">{instance.name}</span>
								),
							},
							{
								header: '',
								content: (instance) => <>{instance.subject}</>,
							},
							{
								header: '',
								content: (instance) => <>{instance.case}</>,
							},
							{
								header: '',
								content: (instance) => (
									<span className="!text-black">
										{formatDateWithin24h(instance.last_message_time)}
									</span>
								),
							},
						]}
						rows={tableData as TableData[]}
						rowActions={(instance) => {
							return [
								{
									label: (
										<div className="flex items-center gap-2">
											<Icon src={Icons.smsNotification} className="!h-5 !w-5" />
											{instance.isRead ? (
												<Typography type="regular-14">
													Mark as Unread
												</Typography>
											) : (
												<Typography type="regular-14">Mark as Read</Typography>
											)}
										</div>
									),
									name: 'mark-as-read-or-unread',
									onClick: (instance) =>
										instance.isRead
											? onClickUnread(instance.id)
											: onClickRead(instance.id),
								},
								{
									label:
										instance.status === 'OPEN' ? (
											<div className="flex items-center gap-2">
												<Icon
													src={Icons.checkCircle}
													className="!h-5 !w-5 !text-primary"
												/>
												<Typography type="regular-14">
													Mark as Resolved
												</Typography>
											</div>
										) : (
											<div className="flex items-center gap-2">
												<CheckCircle className="!text-grey-50 min-w-6 min-h-6" />
												<Typography type="regular-14" className="text-grey-50">
													This conversation has been resolved and closed.
												</Typography>
											</div>
										),
									name:
										instance.status === 'OPEN'
											? 'resolved-conversation'
											: 'placeholder-action',
									onClick:
										instance.status === 'OPEN'
											? () => setResConversationId(instance.id)
											: () => null,
								},
							];
						}}
						totalPages={totalPages}
						classNamePagination={'hidden md:flex'}
					/>
					<MobileInboxList
						tableData={listConversations}
						actions={[
							{
								label: (
									<div className="flex items-center gap-2">
										<Icon src={Icons.smsNotification} className="!h-5 !w-5" />
										<Typography type="regular-14"> Mark as Unread</Typography>
									</div>
								),
								name: 'mark-as-unread',
								onClick: (instance: any) =>
									instance.is_read
										? onClickUnread(instance.id)
										: onClickRead(instance.id),
							},
							{
								label: (
									<div className="flex items-center gap-2">
										<Icon
											src={Icons.checkCircle}
											className="!h-5 !w-5 text-transparent"
										/>
										<Typography type="regular-14">Mark as Resolved</Typography>
									</div>
								),
								name: 'resolved-conversation',
								onClick: (instance: any) => setResConversationId(instance.id),
							},
						]}
					/>
					<ModalResolved
						showButton={false}
						isOpen={!!resConversationId}
						onClickContinue={onClickResolved}
						onClickCancel={() => setResConversationId(undefined)}
					/>
				</>
			)}
		</>
	);
};
